/**
 * TransferReportsModal Container Component
 *
 * Orchestrates the multi-step transfer flow: search → summary → confirm →
 * transferring → success. Each step is a separate View component.
 * The key prop resets all internal state when the modal closes and re-opens.
 * @module features/player-detail/components/TransferReportsModal
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTransferManualPlayerData } from "@/features/player-detail/hooks/useTransferManualPlayerData";
import type {
  TransferStep,
  TransferTargetPlayer,
} from "@/features/player-detail/utils/transferReportsTypes";
import TransferReportsModalView, {
  getStepVisibility,
} from "./TransferReportsModalView";

import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";

/** Props for TransferReportsModalContainer */
export interface TransferReportsModalContainerProps {
  /** Whether the modal is visible */
  readonly isOpen: boolean;
  /** Source manual player ID */
  readonly sourcePlayerId: string;
  /** Source manual player name (for display in summary and confirm) */
  readonly sourcePlayerName: string;
  /** Source player position abbreviation */
  readonly sourcePositionAbbreviation: string | null;
  /** Source player team name */
  readonly sourceTeamName: string | null;
  /** Scout report count on the source player */
  readonly scoutReportCount: number;
  /** Note count on the source player */
  readonly noteCount: number;
  /** Callback to close the modal */
  readonly onClose: () => void;
}

/**
 * Container component orchestrating the transfer reports modal flow
 * @param root0 - Component props
 * @param root0.isOpen - Whether the modal is visible
 * @param root0.sourcePlayerId - ID of the source manual player
 * @param root0.sourcePlayerName - Name of the source manual player
 * @param root0.sourcePositionAbbreviation - Source player position abbreviation
 * @param root0.sourceTeamName - Source player team name
 * @param root0.scoutReportCount - Number of scout reports to transfer
 * @param root0.noteCount - Number of notes to transfer
 * @param root0.onClose - Close callback
 */
const TransferReportsModalContainer = ({
  isOpen,
  sourcePlayerId,
  sourcePlayerName,
  sourcePositionAbbreviation,
  sourceTeamName,
  scoutReportCount,
  noteCount,
  onClose,
}: TransferReportsModalContainerProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);
  const toast = useToastNotification();

  const [step, setStep] = useState<TransferStep>("search");
  const [targetPlayer, setTargetPlayer] = useState<TransferTargetPlayer | null>(
    null
  );
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { transfer } = useTransferManualPlayerData();
  const transferInProgress = useRef(false);

  /** Ref-synced step so handleClose always reads the latest value */
  const stepRef = useRef(step);

  const handleSelectPlayer = useCallback((player: TransferTargetPlayer) => {
    setTargetPlayer(player);
    setStep("summary");
  }, []);

  const handleClose = useCallback(() => {
    const currentStep = stepRef.current;

    /** Ignore close during transfer or after success */
    if (currentStep === "transferring" || currentStep === "success") return;

    setShowCancelConfirm(true);
  }, []);

  const handleCancelConfirm = useCallback(() => {
    setShowCancelConfirm(false);
    setStep("search");
    onClose();
  }, [onClose]);

  const handleCancelDismiss = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  const handleTransferAll = useCallback(() => {
    setStep("confirm");
  }, []);

  const handleConfirm = useCallback(async () => {
    if (transferInProgress.current || !targetPlayer) {
      return;
    }

    transferInProgress.current = true;
    setStep("transferring");

    try {
      await transfer({
        sourcePlayerId,
        targetPlayerId: targetPlayer.id,
      });
      setStep("success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Transfer failed";
      toast.show({ description: message, action: "danger" });
      setStep("confirm");
    } finally {
      transferInProgress.current = false;
    }
  }, [sourcePlayerId, targetPlayer, transfer, toast]);

  const handleBackToSearch = useCallback(() => {
    setStep("search");
  }, []);

  const handleBackToSummary = useCallback(() => {
    setStep("summary");
  }, []);

  const handleDone = useCallback(() => {
    setStep("search");
    onClose();
  }, [onClose]);

  const visibility = useMemo(
    () => getStepVisibility(step, isDark),
    [step, isDark]
  );

  // Side effects
  useLayoutEffect(() => {
    stepRef.current = step;
  }, [step]);

  return (
    <TransferReportsModalView
      key={isOpen ? "open" : "closed"}
      isOpen={isOpen}
      step={step}
      isDark={isDark}
      visibility={visibility}
      sourcePlayerName={sourcePlayerName}
      sourcePositionAbbreviation={sourcePositionAbbreviation}
      sourceTeamName={sourceTeamName}
      scoutReportCount={scoutReportCount}
      noteCount={noteCount}
      targetPlayer={targetPlayer}
      onClose={handleClose}
      onSelectPlayer={handleSelectPlayer}
      onTransferAll={handleTransferAll}
      onConfirm={handleConfirm}
      onBackToSearch={handleBackToSearch}
      onBackToSummary={handleBackToSummary}
      onDone={handleDone}
      showCancelConfirm={showCancelConfirm}
      onCancelConfirm={handleCancelConfirm}
      onCancelDismiss={handleCancelDismiss}
    />
  );
};

TransferReportsModalContainer.displayName = "TransferReportsModalContainer";

export default TransferReportsModalContainer;
